import React from 'react';
import styles from './ProfessionalInfo.module.scss';
import { ImageProfessional } from '../DetailPage/ImageProfessional/ImageProfessional';
import TabsComponent from '../DetailPage/TabsComponent/TabsComponent';
import { ProfileData, profesionStudies } from '../../types';
import { useProfessionalInfo } from './hooks/useProfessionalInfo';
import { ProfessionalFaqsPublic } from './ProfessionalFaqsPublic';
import { Alert, Snackbar } from '@mui/material';
import tagIcon from '../../assets/images/tag.png';
import { useAuthContext } from '../../context/AuthContext';

interface ProfessionalInfoProps {
    professional: ProfileData;
    studies?: profesionStudies[];
    aboutSettings?: { fallback: string; placeholder: string };
    professionalInfoSettings?: { fallback: string; placeholder: string };
}

export const ProfessionalInfo: React.FC<ProfessionalInfoProps> = ({
    professional,
    studies,
    aboutSettings,
    professionalInfoSettings,
}) => {
    const {
        profile,
        fullName,
        mainProfession,
        aboutRaw,
        aboutPreview,
        showFullAbout,
        isLong,
        imageUrl,
        toggleShowFullAbout,
        t,
        handleNativeShare,
        openSnackbar,
        setOpenSnackbar,
        hasUserPurpose,
        displayAboutRaw,
        getTagClass
    } = useProfessionalInfo(professional, aboutSettings, styles);

    const { user } = useAuthContext();
    const isOwnProfile = user?.uid === professional.profile?.uid;

    const specialtyItems = profile.specialties?.length
        ? profile.specialties
        : profile.studies?.filter(
            (s, i, arr) => s.specialty?.uid && arr.findIndex(a => a.specialty?.uid === s.specialty?.uid) === i
        );

    return (
        <main className={styles['prof-info']}>
            <section className={`${styles['prof-info__card']} ${styles['prof-info__card--media']}`}>
                <div className={styles['prof-info__hero-wrapper']}>
                    <ImageProfessional
                        image={imageUrl}
                        mediaType={profile.details?.profile_media_type ?? undefined}
                        videoUrl={profile.details?.profile_video_url ?? undefined}
                    />
                </div>
            </section>

            <section className={`${styles['prof-info__card']} ${styles['prof-info__card--profile']}`}>
                <div className={styles['prof-info__profile-head']}>
                    <div>
                        <h1 className={styles['prof-info__name']}>{fullName}</h1>
                        {mainProfession && (
                            <p className={styles['prof-info__role']}>{mainProfession}</p>
                        )}
                    </div>
                    <div className={styles['prof-info__share']} onClick={handleNativeShare}>
                        {t('landing.professionalInfo.shareProfile', 'Compartir perfil')} ↗

                    </div>
                </div>

                {specialtyItems && specialtyItems.length > 0 && (
                    <div className={styles['prof-info__specialties-row']}>
                        <div className={styles['prof-info__specialties-label']}>
                            <img className={styles['prof-info__icon-tag']} src={tagIcon} alt="" />
                            <span>{t('landing.professionalInfo.specialties', 'Especialidades')}</span>
                        </div>
                        <div className={styles['prof-info__chip-row']}>
                            {specialtyItems.map((item, idx) => (
                                <span key={idx} className={getTagClass(item.specialty?.dimension?.name?.toLowerCase())}>
                                    {item.specialty?.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {profile.details?.is_msp_verified && (
                    <div className={styles['prof-info__meta-line']}>
                        <span className={styles['prof-info__icon-check']}></span>
                        <span>{t('landing.professionalInfo.mspVerified', 'Registro MSP verificado')}</span>
                    </div>
                )}
            </section>

            <section className={`${styles['prof-info__card']} ${styles['prof-info__card--section']}`}>
                <h2 className={styles['prof-info__section-title']}>
                    {t('landing.professionalInfo.purposeTitle', 'Mi propósito y enfoque')}
                </h2>
                <div className={styles['prof-info__copy']}>
                    <div dangerouslySetInnerHTML={{ __html: hasUserPurpose ? (showFullAbout ? aboutRaw : aboutPreview) : displayAboutRaw }} />
                    {hasUserPurpose && isLong && (
                        <button
                            className={styles['prof-info__read-more']}
                            onClick={() => toggleShowFullAbout()}
                        >
                            {showFullAbout ? t('landing.professionalInfo.readLess') : t('landing.professionalInfo.readMore')}
                        </button>
                    )}
                </div>
            </section>

            <TabsComponent
                professionalInfo={profile.details?.professional_info ?? undefined}
                studies={studies ?? profile.studies ?? undefined}
                educationDefaultText={professionalInfoSettings?.fallback}
            />

            <ProfessionalFaqsPublic faqs={(() => {
                let faqsData = profile.faqs;
                if (typeof faqsData === 'string') {
                    try {
                        faqsData = JSON.parse(faqsData);
                    } catch (e) {
                        faqsData = [];
                    }
                }
                return faqsData?.items || faqsData || [];
            })()} isOwnProfile={isOwnProfile} />

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                    {t('landing.professionalsDetail.SocialShare.linkCopied', '¡Enlace copiado!')}
                </Alert>
            </Snackbar>
        </main>
    );
};